// AI Agent Data API Service Layer
import CONF from '@/Config.js'
import axios from 'axios'

class AiAgentDataService {

  // API 호출 헬퍼 함수
  async makeRequest(config) {
    const token = localStorage.getItem("access_token");
    const apiUrl = config.api_url || CONF.API_URL;
    const fullUrl = apiUrl + config.url;

    try {
      const response = await axios({
        method: config.method,
        url: fullUrl,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': config.contentType || 'application/json',
          ...config.headers
        },
        data: config.data,
        params: config.params,
        responseType: config.responseType
      });

      return response;
    } catch (error) {
      const simplifiedError = {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        config: {
          url: error.config?.url,
          method: error.config?.method
        }
      };
      console.error('API Request Error:', simplifiedError);
      throw error;
    }
  }

  // ========== Words API ==========

  // Words 검색 (GET /ai-agent-data/words/search)
  async searchWords(params = {}) {
    //console.log('searchWords', params);
    return await this.makeRequest({
      method: 'GET',
      url: '/ai-agent-data/words/search',
      params: {
        textbook_id: params.textbook_id,
        lesson_id: params.lesson_id,
        word: params.word,
        page: params.page || 1,
        size: params.size || 20
      }
    });
  }

  // Words 단건 조회 (GET /ai-agent-data/words/{item_id})
  async getWord(itemId) {
    //console.log('getWord', itemId);
    return await this.makeRequest({
      method: 'GET',
      url: `/ai-agent-data/words/${itemId}`
    });
  }

  // Words 생성 (POST /ai-agent-data/words/)
  async createWord(data) {
    return await this.makeRequest({
      method: 'POST',
      url: '/ai-agent-data/words/',
      data: {
        textbook_id: data.textbook_id,
        lesson_id: data.lesson_id,
        word: data.word
      }
    });
  }

  // Words 수정 (PUT /ai-agent-data/words/id/{item_id})
  async updateWord(itemId, data) {
    return await this.makeRequest({
      method: 'PUT',
      url: `/ai-agent-data/words/id/${itemId}`,
      data: {
        textbook_id: data.textbook_id,
        lesson_id: data.lesson_id,
        word: data.word
      }
    });
  }

  // Words 삭제 (DELETE /ai-agent-data/words/id/{item_id})
  async deleteWord(itemId) {
    return await this.makeRequest({
      method: 'DELETE',
      url: `/ai-agent-data/words/id/${itemId}`
    });
  }

  // Words CSV 업로드 (POST /ai-agent-data/words/import-csv)
  async importWordsCSV(file) {
    const formData = new FormData();
    formData.append('file', file);

    return await this.makeRequest({
      method: 'POST',
      url: '/ai-agent-data/words/import-csv',
      data: formData,
      contentType: 'multipart/form-data',
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }

  // ========== Expressions API ==========

  // Expressions 검색 (POST /ai-agent-data/expressions/search)
  async searchExpressions(params = {}) {
    return await this.makeRequest({
      method: 'POST',
      url: '/ai-agent-data/expressions/search',
      data: {
        textbook_id: params.textbook_id,
        lesson_id: params.lesson_id,
        expression: params.expression,
        embedding: params.embedding, // 유사도 검색용 (optional)
        similarity_threshold: params.similarity_threshold || 0.7,
        page: params.page || 1,
        size: params.size || 20
      }
    });
  }

  // Expressions 단건 조회 (GET /ai-agent-data/expressions/id/{item_id})
  async getExpression(itemId) {
    return await this.makeRequest({
      method: 'GET',
      url: `/ai-agent-data/expressions/id/${itemId}`
    });
  }

  // Expressions 생성 (POST /ai-agent-data/expressions/)
  async createExpression(data) {
    return await this.makeRequest({
      method: 'POST',
      url: '/ai-agent-data/expressions/',
      data: {
        textbook_id: data.textbook_id,
        lesson_id: data.lesson_id,
        expression: data.expression
        // embedding은 서버가 자동 생성
      }
    });
  }

  // Expressions 수정 (PUT /ai-agent-data/expressions/id/{item_id})
  async updateExpression(itemId, data) {
    return await this.makeRequest({
      method: 'PUT',
      url: `/ai-agent-data/expressions/id/${itemId}`,
      data: {
        textbook_id: data.textbook_id,
        lesson_id: data.lesson_id,
        expression: data.expression
        // embedding은 서버가 자동 재생성
      }
    });
  }

  // Expressions 삭제 (DELETE /ai-agent-data/expressions/id/{item_id})
  async deleteExpression(itemId) {
    return await this.makeRequest({
      method: 'DELETE',
      url: `/ai-agent-data/expressions/id/${itemId}`
    });
  }

  // Expressions CSV 업로드 (POST /ai-agent-data/expressions/import-csv)
  async importExpressionsCSV(file) {
    const formData = new FormData();
    formData.append('file', file);

    return await this.makeRequest({
      method: 'POST',
      url: '/ai-agent-data/expressions/import-csv',
      data: formData,
      contentType: 'multipart/form-data',
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }

  // ========== Sentences API ==========

  // Sentences 검색 (GET /ai-agent-data/sentences/search)
  async searchSentences(params = {}) {
    return await this.makeRequest({
      method: 'GET',
      url: '/ai-agent-data/sentences/search',
      params: {
        textbook_id: params.textbook_id,
        lesson_id: params.lesson_id,
        sentence: params.sentence,
        page: params.page || 1,
        size: params.size || 20
      }
    });
  }

  // Sentences 단건 조회 (GET /ai-agent-data/sentences/id/{item_id})
  async getSentence(itemId) {
    return await this.makeRequest({
      method: 'GET',
      url: `/ai-agent-data/sentences/id/${itemId}`
    });
  }

  // Sentences 생성 (POST /ai-agent-data/sentences/)
  async createSentence(data) {
    return await this.makeRequest({
      method: 'POST',
      url: '/ai-agent-data/sentences/',
      data: {
        textbook_id: data.textbook_id,
        lesson_id: data.lesson_id,
        sentence: data.sentence
        // embedding은 서버가 자동 생성
      }
    });
  }

  // Sentences 수정 (PUT /ai-agent-data/sentences/id/{item_id})
  async updateSentence(itemId, data) {
    return await this.makeRequest({
      method: 'PUT',
      url: `/ai-agent-data/sentences/id/${itemId}`,
      data: {
        textbook_id: data.textbook_id,
        lesson_id: data.lesson_id,
        sentence: data.sentence
        // embedding은 서버가 자동 재생성
      }
    });
  }

  // Sentences 삭제 (DELETE /ai-agent-data/sentences/id/{item_id})
  async deleteSentence(itemId) {
    return await this.makeRequest({
      method: 'DELETE',
      url: `/ai-agent-data/sentences/id/${itemId}`
    });
  }

  // Sentences CSV 업로드 (POST /ai-agent-data/sentences/import-csv)
  async importSentencesCSV(file) {
    const formData = new FormData();
    formData.append('file', file);

    return await this.makeRequest({
      method: 'POST',
      url: '/ai-agent-data/sentences/import-csv',
      data: formData,
      contentType: 'multipart/form-data',
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }
}

export default new AiAgentDataService();
